/**
 * WorkshopSessionsSection — MED-564/MED-566 (E18-8/E18-9)
 * Displays workshops in card or list view with detail side-pane.
 * Supports accordion mode (All tab) and full mode (Workshops tab).
 */

import { useState } from 'react';
import {
  Stack,
  Group,
  Text,
  Box,
  ThemeIcon,
  Badge,
  Button,
  ActionIcon,
  Collapse,
  Avatar,
  SimpleGrid,
  Table,

} from '@mantine/core';
import { Layers, ChevronDown, ChevronRight, Edit2, Calendar, Clock, Link, List, Grid, Users, Send, RefreshCw } from 'react-feather';
import { ButtonToggle } from '../../../../components/common';
import { formatDate, formatTime } from '../../../../utils/formatters';
import type { WorkshopWithSession } from '../../../../services/api/workshops';
import { EditWorkshopModal } from './EditWorkshopModal';
import { WorkshopDetailDrawer } from './WorkshopDetailDrawer';
import { WorkshopFinalizeModal } from './WorkshopFinalizeModal';
import { ModalityConfigPills } from '../ModalityConfigPills';

type WorkshopViewMode = 'card' | 'list';

interface WorkshopSessionsSectionProps {
  cohortId: string;
  workshops: WorkshopWithSession[];
  /** Workshop pattern — sequential shows sequence numbers */
  pattern?: 'sequential' | 'parallel';
  /** When true, renders as accordion with collapse (All tab mode) */
  useAccordion?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
  /** Called after a workshop is successfully updated */
  onWorkshopUpdated: () => void;
  /**
   * MED-1038: the workshop modality's AI features state, surfaced as a read-only
   * pill on the header (always shown — ungated by the live_session_delivery flag).
   */
  aiFeaturesEnabled?: boolean;
}

/**
 * Status badge for a workshop based on session state
 */
function WorkshopStatusBadge({ workshop }: { workshop: WorkshopWithSession }) {
  const status = workshop.session_status;
  if (!status || status === 'unscheduled') {
    return (
      <Badge size="xs" color="gray" variant="light" tt="uppercase" style={{ letterSpacing: '0.3px' }}>
        Draft
      </Badge>
    );
  }
  if (status === 'scheduled') {
    return (
      <Badge size="xs" color="blue" variant="light" tt="uppercase" style={{ letterSpacing: '0.3px' }}>
        Scheduled
      </Badge>
    );
  }
  if (status === 'in_progress') {
    return (
      <Badge size="xs" color="dangerRed" variant="filled" tt="uppercase" style={{ letterSpacing: '0.3px' }}>
        In Progress
      </Badge>
    );
  }
  if (status === 'completed') {
    return (
      <Badge size="xs" color="medleyGreen" variant="light" tt="uppercase" style={{ letterSpacing: '0.3px' }}>
        Completed
      </Badge>
    );
  }
  if (status === 'cancelled') {
    return (
      <Badge size="xs" color="dangerRed" variant="light" tt="uppercase" style={{ letterSpacing: '0.3px' }}>
        Cancelled
      </Badge>
    );
  }
  return (
    <Badge size="xs" color="gray" variant="light" tt="uppercase" style={{ letterSpacing: '0.3px' }}>
      {status}
    </Badge>
  );
}

/**
 * Individual workshop card — zero state or configured state
 */
function WorkshopCard({
  workshop,
  sequential,
  onEdit,
  onClick,
  onFinalize,
  onResend,
}: {
  workshop: WorkshopWithSession;
  sequential: boolean;
  onEdit: (workshop: WorkshopWithSession) => void;
  onClick: (workshop: WorkshopWithSession) => void;
  onFinalize: (workshop: WorkshopWithSession) => void;
  onResend: (workshop: WorkshopWithSession) => void;
}) {
  const isConfigured = !!workshop.scheduled_start;
  const displayTitle = sequential && workshop.sequence_number
    ? `Workshop ${workshop.sequence_number}: ${workshop.title}`
    : workshop.title;
  const hasParticipants = (workshop.participant_count ?? 0) > 0;

  // MED-921 AC1/AC2/AC3: gating for the Finalize / Resend action.
  // - AC1: show "Finalize & Send" only when scheduled and not yet finalized.
  // - AC2: hide entirely when the workshop has no scheduled date/time.
  // - AC3: after finalize, show "Resend Invites" only when at least one
  //   attendee still has a pending RSVP. Otherwise no action button shows.
  const isFinalized = !!workshop.finalized_at;
  const pendingRsvpCount = workshop.pending_rsvp_count ?? 0;
  const showFinalizeButton = isConfigured && !isFinalized;
  const showResendButton = isConfigured && isFinalized && pendingRsvpCount > 0;

  return (
    <Box
      onClick={() => onClick(workshop)}
      style={{
        border: '1px solid #E8E6E1',
        borderRadius: 8,
        padding: '16px',
        backgroundColor: '#fff',
        cursor: 'pointer',
        transition: 'box-shadow 0.15s ease',
        width: '100%',
        textAlign: 'left',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.boxShadow = 'none';
      }}
    >
      <Stack gap="sm">
          {/* Header row: title + status badge + edit button */}
          <Group justify="space-between" wrap="nowrap">
            <Group gap="sm" style={{ flex: 1, minWidth: 0 }}>
              <Text fw={600} size="sm" style={{ flex: 1 }} lineClamp={1}>
                {displayTitle}
              </Text>
              <WorkshopStatusBadge workshop={workshop} />
            </Group>
            <Button
              size="xs"
              variant="default"
              radius="sm"
              leftSection={<Edit2 size={12} />}
              onClick={(e) => {
                e.stopPropagation();
                onEdit(workshop);
              }}
              style={{ flexShrink: 0 }}
            >
              Edit
            </Button>
          </Group>

          {/* Date/time */}
          <Group gap="xs" wrap="nowrap">
            <Calendar size={13} color={isConfigured ? '#444' : '#aaa'} />
            {isConfigured ? (
              <Text size="xs" c="dark">
                {formatDate(workshop.scheduled_start!)} · {formatTime(workshop.scheduled_start!)}
              </Text>
            ) : (
              <Text size="xs" c="dimmed" fs="italic">
                No date set
              </Text>
            )}
          </Group>

          {/* Duration */}
          {workshop.session_duration_minutes && (
            <Group gap="xs" wrap="nowrap">
              <Clock size={13} color="#888" />
              <Text size="xs" c="dimmed">
                {workshop.session_duration_minutes} min
              </Text>
            </Group>
          )}

          {/* Meeting link */}
          {workshop.zoom_link && (
            <Group gap="xs" wrap="nowrap">
              <Link size={13} color="#888" />
              <Text
                size="xs"
                c="blue"
                component="a"
                href={workshop.zoom_link}
                target="_blank"
                rel="noopener noreferrer"
                style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                onClick={(e) => e.stopPropagation()}
              >
                Meeting link
              </Text>
            </Group>
          )}

          {/* Facilitator */}
          <Group gap="xs" wrap="nowrap">
            {workshop.facilitator_id ? (
              <>
                <Avatar size={22} radius="xl" color="medleyGreen" style={{ flexShrink: 0 }}>
                  {(workshop.facilitator_name || 'F').charAt(0).toUpperCase()}
                </Avatar>
                <Text size="xs" c="dark">
                  {workshop.facilitator_name}
                </Text>
              </>
            ) : (
              <>
                <Avatar
                  size={22}
                  radius="xl"
                  style={{
                    flexShrink: 0,
                    border: '1.5px dashed #bbb',
                    backgroundColor: 'transparent',
                  }}
                />
                <Text size="xs" c="dimmed" fs="italic">
                  No facilitator assigned
                </Text>
              </>
            )}
          </Group>

          {/* Participant count / zero state CTA */}
          <Group gap="xs" wrap="nowrap">
            <Users size={13} color="#888" />
            {hasParticipants ? (
              <Text size="xs" c="dimmed">
                {workshop.participant_count} participant{workshop.participant_count !== 1 ? 's' : ''}
              </Text>
            ) : (
              <Text size="xs" c="blue" style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                Click to add participants
              </Text>
            )}
          </Group>

          {/* MED-921: Finalize & Send / Resend Invites action */}
          {(showFinalizeButton || showResendButton) && (
            <Group justify="flex-end" gap="xs" mt={4}>
              {showFinalizeButton && (
                <Button
                  size="xs"
                  variant="filled"
                  radius="sm"
                  leftSection={<Send size={12} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onFinalize(workshop);
                  }}
                >
                  Finalize &amp; Send
                </Button>
              )}
              {showResendButton && (
                <Button
                  size="xs"
                  variant="filled"
                  radius="sm"
                  leftSection={<RefreshCw size={12} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onResend(workshop);
                  }}
                >
                  Resend Invites
                </Button>
              )}
            </Group>
          )}
        </Stack>
    </Box>
  );
}

/**
 * List view row for a single workshop
 */
function WorkshopListRow({
  workshop,
  sequential,
  onEdit,
  onClick,
  onFinalize,
  onResend,
}: {
  workshop: WorkshopWithSession;
  sequential: boolean;
  onEdit: (workshop: WorkshopWithSession) => void;
  onClick: (workshop: WorkshopWithSession) => void;
  onFinalize: (workshop: WorkshopWithSession) => void;
  onResend: (workshop: WorkshopWithSession) => void;
}) {
  const displayTitle = sequential && workshop.sequence_number
    ? `Workshop ${workshop.sequence_number}: ${workshop.title}`
    : workshop.title;

  // MED-921 AC1/AC2/AC3: same gating as the card variant
  const isConfigured = !!workshop.scheduled_start;
  const isFinalized = !!workshop.finalized_at;
  const pendingRsvpCount = workshop.pending_rsvp_count ?? 0;
  const showFinalizeButton = isConfigured && !isFinalized;
  const showResendButton = isConfigured && isFinalized && pendingRsvpCount > 0;

  return (
    <Table.Tr
      style={{ cursor: 'pointer' }}
      onClick={() => onClick(workshop)}
    >
      <Table.Td>
        <Text size="sm" fw={500} lineClamp={1}>{displayTitle}</Text>
      </Table.Td>
      <Table.Td>
        {workshop.scheduled_start ? (
          <Text size="sm" c="dimmed">
            {formatDate(workshop.scheduled_start)} · {formatTime(workshop.scheduled_start)}
          </Text>
        ) : (
          <Text size="xs" c="dimmed" fs="italic">No date set</Text>
        )}
      </Table.Td>
      <Table.Td>
        {workshop.facilitator_id ? (
          <Group gap="xs" wrap="nowrap">
            <Avatar size={22} radius="xl" color="medleyGreen">
              {(workshop.facilitator_name || 'F').charAt(0).toUpperCase()}
            </Avatar>
            <Text size="sm">{workshop.facilitator_name}</Text>
          </Group>
        ) : (
          <Text size="xs" c="dimmed" fs="italic">Unassigned</Text>
        )}
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">{workshop.participant_count ?? 0}</Text>
      </Table.Td>
      <Table.Td>
        <WorkshopStatusBadge workshop={workshop} />
      </Table.Td>
      <Table.Td>
        <Group gap="xs" wrap="nowrap">
          <Button
            size="xs"
            variant="default"
            radius="sm"
            leftSection={<Edit2 size={12} />}
            onClick={(e) => {
              e.stopPropagation();
              onEdit(workshop);
            }}
          >
            Edit
          </Button>
          {showFinalizeButton && (
            <Button
              size="xs"
              variant="filled"
              radius="sm"
              leftSection={<Send size={12} />}
              onClick={(e) => {
                e.stopPropagation();
                onFinalize(workshop);
              }}
            >
              Finalize &amp; Send
            </Button>
          )}
          {showResendButton && (
            <Button
              size="xs"
              variant="filled"
              radius="sm"
              leftSection={<RefreshCw size={12} />}
              onClick={(e) => {
                e.stopPropagation();
                onResend(workshop);
              }}
            >
              Resend Invites
            </Button>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}

export function WorkshopSessionsSection({
  cohortId,
  workshops,
  pattern = 'parallel',
  useAccordion = false,
  isExpanded = true,
  onToggle,
  onWorkshopUpdated,
  aiFeaturesEnabled,
}: WorkshopSessionsSectionProps) {
  const [viewMode, setViewMode] = useState<WorkshopViewMode>('card');
  const [editWorkshop, setEditWorkshop] = useState<WorkshopWithSession | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [detailWorkshop, setDetailWorkshop] = useState<WorkshopWithSession | null>(null);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  // MED-921: finalize / resend modal state
  const [finalizeWorkshop, setFinalizeWorkshop] = useState<WorkshopWithSession | null>(null);
  const [finalizeModalOpen, setFinalizeModalOpen] = useState(false);
  const [finalizeMode, setFinalizeMode] = useState<'finalize' | 'resend'>('finalize');

  const sequential = pattern === 'sequential';
  const workshopCount = workshops.length;
  const subtitle = `${workshopCount} workshop${workshopCount !== 1 ? 's' : ''} · ${sequential ? 'Sequential' : 'Parallel'}`;

  const handleCardClick = (workshop: WorkshopWithSession) => {
    setDetailWorkshop(workshop);
    setDetailDrawerOpen(true);
  };

  const handleOpenFinalize = (workshop: WorkshopWithSession) => {
    setFinalizeWorkshop(workshop);
    setFinalizeMode('finalize');
    setFinalizeModalOpen(true);
  };

  const handleOpenResend = (workshop: WorkshopWithSession) => {
    setFinalizeWorkshop(workshop);
    setFinalizeMode('resend');
    setFinalizeModalOpen(true);
  };

  const sectionHeader = (
    <Group
      justify="space-between"
      style={{
        backgroundColor: 'var(--medley-surface-warm, #F5F4F0)',
        padding: '16px 20px',
        cursor: useAccordion && onToggle ? 'pointer' : 'default',
        borderBottom: isExpanded ? '1px solid #E5E2DC' : 'none',
      }}
      onClick={useAccordion && onToggle ? onToggle : undefined}
      wrap="nowrap"
    >
      <Group gap="sm">
        <ThemeIcon color="grape" size={40} radius="md" variant="filled">
          <Layers size={20} />
        </ThemeIcon>
        <Box>
          <Text fw={600} size="16px">Workshops</Text>
          <Text size="13px" c="dimmed" mt={4}>{subtitle}</Text>
        </Box>
        {/* MED-1038: read-only AI pill (Enabled/Off), grouped with the title */}
        <ModalityConfigPills modality={{ modality_type: 'workshop', ai_features_enabled: aiFeaturesEnabled }} />
      </Group>

      <Group gap="sm" onClick={(e) => e.stopPropagation()}>
        {/* List / Card toggle — only show in full (non-accordion) mode */}
        {!useAccordion && workshops.length > 0 && (
          <ButtonToggle
            options={[
              { value: 'list' as const, icon: List, ariaLabel: 'List view' },
              { value: 'card' as const, icon: Grid, ariaLabel: 'Card view' },
            ]}
            value={viewMode}
            onChange={setViewMode}
            size="sm"
          />
        )}

        {useAccordion && onToggle && (
          <ActionIcon variant="subtle" color="gray" onClick={(e) => { e.stopPropagation(); onToggle(); }}>
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </ActionIcon>
        )}
      </Group>
    </Group>
  );

  const cardView = (
    <Box p="md">
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="sm">
        {workshops.map((w) => (
          <WorkshopCard
            key={w.id}
            workshop={w}
            sequential={sequential}
            onEdit={(workshop) => {
              setEditWorkshop(workshop);
              setEditModalOpen(true);
            }}
            onClick={handleCardClick}
            onFinalize={handleOpenFinalize}
            onResend={handleOpenResend}
          />
        ))}
      </SimpleGrid>
    </Box>
  );

  const listView = (
    <Box p="md">
      <Table highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Workshop</Table.Th>
            <Table.Th>Date & Time</Table.Th>
            <Table.Th>Facilitator</Table.Th>
            <Table.Th>Participants</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {workshops.map((w) => (
            <WorkshopListRow
              key={w.id}
              workshop={w}
              sequential={sequential}
              onEdit={(workshop) => {
                setEditWorkshop(workshop);
                setEditModalOpen(true);
              }}
              onClick={handleCardClick}
              onFinalize={handleOpenFinalize}
              onResend={handleOpenResend}
            />
          ))}
        </Table.Tbody>
      </Table>
    </Box>
  );

  const emptyState = (
    <Box p="md">
      <Stack align="center" py="xl" gap="sm">
        <ThemeIcon size={48} radius={48} variant="light" color="grape">
          <Layers size={24} />
        </ThemeIcon>
        <Text fw={600}>No workshops configured</Text>
        <Text size="sm" c="dimmed">
          Workshop sessions will appear here once the workshop modality is configured.
        </Text>
      </Stack>
    </Box>
  );

  const workshopContent = workshops.length === 0
    ? emptyState
    : (useAccordion || viewMode === 'card') ? cardView : listView;

  const content = (
    <>
      <Box
        style={{
          border: useAccordion ? '1px solid #E5E2DC' : undefined,
          borderRadius: useAccordion ? 8 : undefined,
          overflow: 'hidden',
        }}
      >
        {sectionHeader}
        {useAccordion ? (
          <Collapse in={isExpanded}>
            <Box style={{ backgroundColor: '#fff' }}>
              {workshopContent}
            </Box>
          </Collapse>
        ) : (
          workshopContent
        )}
      </Box>

      <EditWorkshopModal
        opened={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        cohortId={cohortId}
        workshop={editWorkshop}
        onSaved={() => {
          setEditModalOpen(false);
          onWorkshopUpdated();
        }}
      />

      <WorkshopDetailDrawer
        opened={detailDrawerOpen}
        onClose={() => setDetailDrawerOpen(false)}
        cohortId={cohortId}
        workshop={detailWorkshop}
        sequential={sequential}
        onWorkshopUpdated={() => {
          onWorkshopUpdated();
          // Refresh the detail workshop data after update
        }}
      />

      {/* MED-921: Finalize / Resend workshop modal */}
      <WorkshopFinalizeModal
        opened={finalizeModalOpen}
        onClose={() => setFinalizeModalOpen(false)}
        cohortId={cohortId}
        workshop={finalizeWorkshop}
        mode={finalizeMode}
        onFinalized={() => {
          setFinalizeModalOpen(false);
          onWorkshopUpdated();
        }}
      />
    </>
  );

  if (!useAccordion) {
    return <Stack gap={0}>{content}</Stack>;
  }

  return content;
}

export default WorkshopSessionsSection;
